import { uiText } from '../utils/i18n';
import { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { useStore } from '../store';
import { Avatar } from './shared/Avatar';
import { useT } from '../utils/i18n';
import { MEMORY_UPLOAD_OPTIONS, type MemoryUploadScope } from './memory-upload-options';
import styles from './MemoryPage.module.css';

type DocScope = MemoryUploadScope;

interface SharedDoc {
  id: string;
  title: string;
  path: string;
  tags: string[];
  created_by: string;
  updated_at: string;
  content?: string;
  size?: number;
  scope?: DocScope;
  owner_id?: string;
}

interface ConfirmState {
  title: string;
  message: string;
  variant: 'danger' | 'warn';
  onConfirm: () => void;
}

interface ActivityEntry {
  action: 'upload' | 'delete';
  who: string;
  doc: string;
  when: number; // timestamp
  scope?: DocScope;
}

// Cover color — stable hash based on doc id so color never changes
const COVER_COLORS = ['#e34a2a', '#5a6a2a', '#c79a3a', '#5a8ab8', '#f4a48a', '#b6c477'];
const COVER_ICONS  = ['\u2606', '\u2726', '\u2690', '\u26A1', '\u25D0', '\u25C7', '\u2605', '\u266B'];

function stableHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
function getCoverColor(id: string) { return COVER_COLORS[stableHash(id) % COVER_COLORS.length]; }
function getCoverIcon(id: string) { return COVER_ICONS[stableHash(id + '_icon') % COVER_ICONS.length]; }

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return uiText("刚刚");
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/* ---- Stat tile ---- */
function StatTile({ label, value, sub, subColor }: { label: string; value: string; sub: string; subColor?: string }) {
  return (
    <div className={styles.statTile}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValueRow}>
        <div className={styles.statValue}>{value}</div>
        <div className={styles.statSub} style={{ color: subColor || 'var(--olive)' }}>{sub}</div>
      </div>
    </div>
  );
}

/* ---- Document card (book cover style) ---- */
function DocCard({ doc, onClick }: { doc: SharedDoc; onClick: () => void }) {
  const color = getCoverColor(doc.id);
  const icon = getCoverIcon(doc.id);
  const sizeStr = doc.size ? formatFileSize(doc.size) : '';
  return (
    <div className={styles.card} onClick={onClick}>
      {/* book cover */}
      <div className={styles.cardCover} style={{ background: color }}>
        <div className={styles.cardSpine} />
        <div className={styles.cardGlyph}>{icon}</div>
        <div className={styles.cardPathTag}>{doc.path}</div>
      </div>

      {/* body */}
      <div className={styles.cardBody}>
        <h3 className={styles.cardTitle}>{doc.title}</h3>
        <div className={styles.cardMetaRow}>
          <span className={styles.cardFormatBadge}>MD</span>
          {sizeStr && <span className={styles.cardSize}>{sizeStr}</span>}
        </div>
        <div className={styles.cardFooter}>
          <span className={styles.cardAuthorDot} />
          <span>{doc.created_by || uiText("未知")}</span>
          <span className={styles.cardDate}>{new Date(doc.updated_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

/* ---- Activity rail ---- */
function ActivityRail({ activities, avatarMap, scope }: { activities: ActivityEntry[]; avatarMap: Map<string, string>; scope: DocScope }) {
  const filtered = activities.filter(a => scope === 'personal' ? a.scope === 'personal' : a.scope !== 'personal').slice(0, 10);

  return (
    <aside className={styles.activityRail}>
      <div className={styles.activityHeader}>
        <span className={styles.eyebrow}>{scope === 'personal' ? uiText("个人知识库日志") : uiText("组织知识库日志")}</span>
      </div>
      <div className={styles.activityList}>
        {filtered.length === 0 && (
          <div className={styles.activityEmpty}>{uiText("暂无记录")}</div>
        )}
        {filtered.map((a, i) => (
          <div key={i} className={styles.activityItem}>
            <Avatar name={a.who || 'U'} size="sm" imageUrl={avatarMap.get(a.who) || undefined} />
            <div className={styles.activityBody}>
              <div className={styles.activityText}>
                <b>{a.who || uiText("未知")}</b>{' '}
                <span style={{ color: 'var(--text-dim)' }}>{a.action === 'upload' ? uiText("上传了") : uiText("移除了")}</span>{' '}
                <span className={styles.activityDoc}>{a.doc}</span>
              </div>
              <div className={styles.activityTime}>{timeAgo(a.when)} {uiText("前")}</div>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

/* ==== Main component ==== */
export function MemoryPage() {
  const [docs, setDocs] = useState<SharedDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SharedDoc[] | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<SharedDoc | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [toast, setToast] = useState('');
  const [dragging, setDragging] = useState(false);
  const [orgStats, setOrgStats] = useState<{ doc_count: number; editor_count: number; total_size: number }>({ doc_count: 0, editor_count: 0, total_size: 0 });
  const [personalStats, setPersonalStats] = useState<{ doc_count: number; total_size: number }>({ doc_count: 0, total_size: 0 });
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [scope, setScope] = useState<DocScope>('org');
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const token = useStore((s) => s.token);
  const userName = useStore((s) => s.userName);
  const userAvatar = useStore((s) => s.userAvatar);
  const userId = typeof window !== 'undefined' ? localStorage.getItem('novax:userId') || '' : '';
  const t = useT();

  // Fetch workspace members to build name→avatar map for activity rail
  const [avatarMap, setAvatarMap] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch('/auth/workspace/members', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (cancelled || !data?.members) return;
        const map = new Map<string, string>();
        for (const m of data.members) {
          if (m.name && m.avatarUrl) map.set(m.name, m.avatarUrl);
        }
        // Ensure current user's avatar is always in the map
        if (userName && userAvatar && !map.has(userName)) {
          map.set(userName, userAvatar);
        }
        setAvatarMap(map);
        // Check if current user is admin/owner
        const email = useStore.getState().userEmail;
        const me = (data.members || []).find((m: any) => m.email === email);
        setIsAdmin(me?.role === 'owner' || me?.role === 'admin');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const fetchStats = useCallback(async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [orgRes, personalRes] = await Promise.all([
        fetch('/memory/api/health?scope=org', { headers }),
        userId ? fetch(`/memory/api/health?scope=personal&owner_id=${encodeURIComponent(userId)}`, { headers }) : null,
      ]);
      if (orgRes.ok) {
        const d = await orgRes.json();
        setOrgStats({ doc_count: d.document_count || 0, editor_count: d.editor_count || 0, total_size: d.total_size || 0 });
      }
      if (personalRes?.ok) {
        const d = await personalRes.json();
        setPersonalStats({ doc_count: d.document_count || 0, total_size: d.total_size || 0 });
      }
    } catch { /* ignore */ }
  }, [token, userId]);

  const fetchActivities = useCallback(async () => {
    try {
      const res = await fetch(`/memory/api/activities?limit=20&scope=${scope}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const list = await res.json();
        setActivities(list.map((a: any) => ({ action: a.action, who: a.who, doc: a.doc_title, when: new Date(a.created_at).getTime(), scope: a.scope || 'org' })));
      }
    } catch { /* ignore */ }
  }, [token, scope]);

  const addActivity = useCallback(async (action: 'upload' | 'delete', docTitle: string, actScope: DocScope = 'org') => {
    // Optimistic update
    setActivities((prev) => [{ action, who: userName || '未知', doc: docTitle, when: Date.now(), scope: actScope }, ...prev].slice(0, 20));
    try {
      await fetch('/memory/api/activities', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, doc_title: docTitle, scope: actScope }),
      });
    } catch { /* ignore */ }
  }, [userName, token]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const memoryUrl = '/memory/api';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = scope === 'personal' ? '?scope=personal' : '?scope=org';
      const res = await fetch(`${memoryUrl}/documents${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setDocs(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [token, scope]);

  useEffect(() => {
    fetchData();
    fetchActivities();
    fetchStats();
  }, [fetchData, fetchActivities, fetchStats]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    try {
      const scopeParam = scope === 'personal' ? '&scope=personal' : '&scope=org';
      const res = await fetch(`${memoryUrl}/search?q=${encodeURIComponent(searchQuery)}${scopeParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSearchResults(await res.json());
    } catch { /* ignore */ }
  }, [searchQuery, token, scope]);

  const fetchDoc = useCallback(async (docId: string) => {
    setDocLoading(true);
    try {
      const res = await fetch(`${memoryUrl}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSelectedDoc(await res.json());
      else showToast(uiText("文档读取失败或无权访问"));
    } catch { showToast(uiText("文档读取失败")); } finally { setDocLoading(false); }
  }, [token, showToast]);

  const doUpload = useCallback(async (file: File, uploadScope: DocScope) => {
    setUploading(true);
    try {
      const content = await file.text();
      const title = file.name.replace(/\.md$/, '');
      const existing = docs.find((doc) => doc.title === title && (doc.scope || 'org') === uploadScope);
      const body = { title, content, tags: [], scope: uploadScope };
      const res = await fetch(existing ? `${memoryUrl}/documents/${existing.id}` : `${memoryUrl}/documents`, {
        method: existing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        addActivity('upload', title, uploadScope);
        await fetchData();
        fetchStats();
        setSearchResults(null);
        setSearchQuery('');
        showToast(existing ? uiText('文档已覆盖') : uiText('文档已上传'));
      } else {
        const error = await res.json().catch(() => ({}));
        showToast(uiText(error.detail || '上传失败'));
      }
    } catch (err) { console.error(err); showToast(uiText("上传失败")); }
    setUploading(false);
  }, [token, fetchData, fetchStats, docs, addActivity, showToast]);

  const uploadScopeRef = useRef<DocScope>('org');

  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.md')) { showToast(uiText("仅支持 .md 文件")); return; }
    if (file.size > 2 * 1024 * 1024) { showToast(uiText("文件过大，最大支持 2MB")); return; }
    const title = file.name.replace(/\.md$/, '');
    const scopeToUse = uploadScopeRef.current;
    if (docs.some(d => d.title === title && (d.scope || 'org') === scopeToUse)) {
      setConfirmState({
        title: uiText('覆盖确认'),
        message: `${uiText('同名文档')}「${title}」${uiText('已存在，是否覆盖？')}`,
        variant: 'warn',
        onConfirm: () => { setConfirmState(null); doUpload(file, scopeToUse); },
      });
      return;
    }
    doUpload(file, scopeToUse);
  }, [docs, doUpload, showToast]);

  const handleUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    processFile(file);
  }, [processFile]);

  const doDelete = useCallback(async (docId: string) => {
    setDeleting(true);
    const doc = docs.find(d => d.id === docId);
    const docTitle = doc?.title || uiText('未知文档');
    const docScope = doc?.scope || 'org';
    try {
      const res = await fetch(`${memoryUrl}/documents/${docId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        addActivity('delete', docTitle, docScope);
        if (selectedDoc?.id === docId) setSelectedDoc(null);
        await fetchData();
        fetchStats();
        setSearchResults(null);
        setSearchQuery('');
        showToast(uiText("文档已删除"));
      } else {
        showToast(uiText("删除失败或无权操作"));
      }
    } catch (err) { console.error(err); showToast(uiText("删除失败")); } finally { setDeleting(false); }
  }, [token, fetchData, fetchStats, selectedDoc, docs, addActivity, showToast]);

  const handleDelete = useCallback((docId: string) => {
    setConfirmState({
      title: t('memory.deleteConfirm'),
      message: uiText('删除后无法恢复，确定要删除这个文档吗？'),
      variant: 'danger',
      onConfirm: () => { setConfirmState(null); doDelete(docId); },
    });
  }, [t, doDelete]);

  // Drag & drop
  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setDragging(true); }, []);
  const handleDragLeave = useCallback(() => setDragging(false), []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    uploadScopeRef.current = scope;
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile, scope]);

  const displayDocs = searchResults || docs;

  return (
    <div
      className={styles.container}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragging && <div className={styles.dragOverlay}>{uiText("松开以上传 .md 文件")}</div>}

      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.eyebrow}>knowledge base</span>
          <h1 className={styles.title}>{uiText("知识库")}</h1>
        </div>
        <div className={styles.headerSpacer} />
        <div className={styles.searchBox}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="6" stroke="var(--ink)" strokeWidth="2" />
            <line x1="16" y1="16" x2="21" y2="21" stroke="var(--ink)" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            className={styles.searchInput}
            placeholder={t('memory.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <span className={styles.searchKbd}>&#8984;K</span>
        </div>
      </header>

      {/* Body */}
      <div className={styles.body}>
        {/* Upload hero */}
        <div className={styles.uploadHero}>
          <div className={styles.heroCircleBig} />
          <div className={styles.heroCircleSmall} />
          <div className={styles.heroContent}>
            <span className={styles.eyebrow} style={{ color: 'var(--olive-lt)' }}>{uiText("知识库 &middot; Knowledge Base")}</span>
            <div className={styles.heroTitle}>
              {uiText("团队的")}<span className={styles.heroHighlightRed}>{uiText("共同记忆")}</span>，<br />
              {uiText("也是 Agent 们的")}<span className={styles.heroHighlightGreen}>{uiText("共同规则")}</span>。
            </div>
          </div>
          <div className={styles.uploadAction}>
            <div className={styles.uploadIconBox}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                <path d="M12 4v12M6 10l6-6 6 6M4 20h16" stroke="var(--bg-paper)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className={styles.uploadTexts}>
              <div className={styles.uploadLabel}>{uiText("拖入 .md 文件，或点击上传")}</div>
              <div className={styles.uploadHint}>{uiText("当前仅支持 .md 格式")}</div>
            </div>
            <div className={styles.uploadBtnWrap}>
              <button
                className={styles.uploadBtn}
                style={uploading ? { opacity: 0.5 } : undefined}
                disabled={uploading}
                onClick={() => setUploadMenuOpen((v) => !v)}
              >
                {uploading ? '...' : uiText("上传")}
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ marginLeft: 4 }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Stats row — always visible, shows both org + personal */}
        <div className={styles.statsRow}>
          <StatTile label={uiText("组织文档数")} value={String(orgStats.doc_count)} sub={uiText("篇")} />
          <StatTile label={uiText("组织编辑人数")} value={String(orgStats.editor_count)} sub={uiText("人")} />
          <StatTile label={uiText("组织存储")} value={orgStats.total_size > 0 ? formatFileSize(orgStats.total_size) : '--'} sub="" subColor="var(--text-dim)" />
          <StatTile label={uiText("个人文档数")} value={String(personalStats.doc_count)} sub={uiText("篇")} />
          <StatTile label={uiText("个人存储")} value={personalStats.total_size > 0 ? formatFileSize(personalStats.total_size) : '--'} sub="" subColor="var(--text-dim)" />
        </div>

        {/* Below: tabs + content + activity rail */}
        <div className={styles.mainArea} style={{ opacity: docLoading ? 0.5 : 1, pointerEvents: docLoading ? 'none' : 'auto' }}>
          <div className={styles.mainLeft}>
            {/* Scope tabs */}
            <div className={styles.scopeTabs}>
              <button
                className={`${styles.scopeTab} ${scope === 'org' ? styles.scopeTabActive : ''}`}
                onClick={() => { setScope('org'); setSearchResults(null); setSearchQuery(''); }}
              >
                {uiText("组织知识库")}
              </button>
              <button
                className={`${styles.scopeTab} ${scope === 'personal' ? styles.scopeTabActive : ''}`}
                onClick={() => { setScope('personal'); setSearchResults(null); setSearchQuery(''); }}
              >
                {uiText("我的知识库")}
              </button>
            </div>

            {/* Loading / empty / grid */}
            {loading ? (
              <div className={styles.loading}>{t('memory.loading')}</div>
            ) : displayDocs.length === 0 ? (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>&#128218;</div>
                <div className={styles.emptyTitle}>{t('memory.empty')}</div>
                <div className={styles.emptyHint}>{t('memory.emptyHint')}</div>
              </div>
            ) : (
              <div className={styles.grid}>
                {displayDocs.map((doc) => (
                  <DocCard key={doc.id} doc={doc} onClick={() => fetchDoc(doc.id)} />
                ))}
              </div>
            )}
          </div>

          <ActivityRail activities={activities} avatarMap={avatarMap} scope={scope} />
        </div>
      </div>

      {/* Upload destination chooser — rendered outside the clipped hero banner. */}
      {uploadMenuOpen && (
        <div className={styles.uploadScopeOverlay} onClick={() => setUploadMenuOpen(false)}>
          <div
            className={styles.uploadScopeDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="memory-upload-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.uploadScopeHeader}>
              <div>
                <span className={styles.eyebrow}>UPLOAD DESTINATION</span>
                <h3 id="memory-upload-title" className={styles.uploadScopeTitle}>{uiText("上传到哪里？")}</h3>
              </div>
              <button className={styles.uploadScopeClose} onClick={() => setUploadMenuOpen(false)} aria-label={uiText("关闭上传选择")}>
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className={styles.uploadScopeChoices}>
              {MEMORY_UPLOAD_OPTIONS.map((option) => (
                <label key={option.scope} className={styles.uploadScopeChoice}>
                  <span className={styles.uploadScopeChoiceTop}>
                    <span className={styles.uploadScopeChoiceIcon}>{option.scope === 'personal' ? uiText("我") : uiText("组")}</span>
                    <span className={styles.uploadScopeBadge}>{option.badge}</span>
                  </span>
                  <strong>{option.title}</strong>
                  <span className={styles.uploadScopeDescription}>{option.description}</span>
                  <span className={styles.uploadScopeAction}>{uiText("选择 .md 文件 →")}</span>
                  <input
                    type="file"
                    accept=".md"
                    className={styles.hiddenFileInput}
                    onChange={(event) => {
                      uploadScopeRef.current = option.scope;
                      setUploadMenuOpen(false);
                      handleUpload(event);
                    }}
                    disabled={uploading}
                  />
                </label>
              ))}
            </div>
            <p className={styles.uploadScopeFootnote}>{uiText("上传后仍可在“组织知识库 / 我的知识库”中分别查看。")}</p>
          </div>
        </div>
      )}

      {/* Document viewer modal */}
      {selectedDoc && (
        <div className={styles.docOverlay} onClick={() => setSelectedDoc(null)}>
          <div className={styles.docPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.docHeader}>
              <div className={styles.docHeaderInfo}>
                <h2 className={styles.docTitle}>{selectedDoc.title}</h2>
                <div className={styles.docMeta}>
                  {selectedDoc.created_by && <span>{selectedDoc.created_by}</span>}
                  <span>{new Date(selectedDoc.updated_at).toLocaleDateString()}</span>
                </div>
              </div>
              <div className={styles.docActions}>
                <button className={styles.docDeleteBtn} onClick={() => {
                  const docScope = selectedDoc.scope || 'org';
                  if (docScope === 'org' && !isAdmin) { showToast(uiText("仅管理员可删除组织知识库文档")); return; }
                  handleDelete(selectedDoc.id);
                }} disabled={deleting} title={uiText("删除文档")}>
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  </svg>
                </button>
                <button className={styles.docCloseBtn} onClick={() => setSelectedDoc(null)}>
                  <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
            {selectedDoc.tags?.length > 0 && (
              <div className={styles.docTags}>
                {selectedDoc.tags.map((tag) => (
                  <span key={tag} className={styles.tag}>{tag}</span>
                ))}
              </div>
            )}
            <div className={styles.docBody}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                {selectedDoc.content || ''}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmState && (
        <div className={styles.confirmOverlay} onClick={() => setConfirmState(null)}>
          <div className={styles.confirmBox} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>{confirmState.title}</h3>
            <p className={styles.confirmMsg}>{confirmState.message}</p>
            <div className={styles.confirmActions}>
              <button className={styles.confirmCancel} onClick={() => setConfirmState(null)}>{uiText("取消")}</button>
              <button
                className={confirmState.variant === 'danger' ? styles.confirmOk : styles.confirmOkWarn}
                onClick={confirmState.onConfirm}
              >{uiText("确定")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div className={styles.toast}>{toast}</div>}
    </div>
  );
}
